import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X,
  Plus,
  Minus,
  FileSpreadsheet,
  PhoneOff,
  DollarSign,
  BarChart3,
  Workflow,
  UserX,
  BadgeCheck,
  UserCheck,
  Headphones,
  CalendarCheck,
} from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import useReveal from "../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Shared --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({
  children,
  testid,
  href = CALENDLY,
  external = true,
  as = "a",
}) => {
  const cls =
    "group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all";
  if (as === "link") {
    return (
      <Link to={href} data-testid={testid} className={cls}>
        {children}
        <ArrowRight
          size={16}
          className="group-hover:translate-x-0.5 transition-transform"
        />
      </Link>
    );
  }
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className={cls}
    >
      {children}
      <ArrowRight
        size={16}
        className="group-hover:translate-x-0.5 transition-transform"
      />
    </a>
  );
};

const SecondaryCTA = ({ children, testid, to }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center justify-center rounded-lg bg-white text-[#0A1628] border border-slate-300 hover:border-[#0A1628] px-6 py-3.5 text-[15px] font-semibold transition-colors"
  >
    {children}
  </Link>
);

/* ---------------------------------- Hero ---------------------------------- */

const HERO_CHIPS = [
  "Trained in QuickBooks, ServiceTitan & GoHighLevel",
  "Flat monthly fee — no hourly surprises",
  "Live in 2–3 weeks",
];

const Hero = () => (
  <section
    id="top"
    data-testid="home-hero"
    className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden"
  >
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>

    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          <Eyebrow>Built for Trades &amp; Home Services</Eyebrow>
          <h1 data-testid="hero-headline" className="h1-hero mt-5 text-[#0A1628]">
            Your crew handles the job.{" "}
            <span className="text-[#16A06A]">We handle everything else.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-[620px]">
            A done-for-you virtual operations team trained specifically for
            trades and home services. We hire, employ, and train the people.
            You direct the work. One flat monthly fee.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <PrimaryCTA testid="hero-cta-primary">
              Get a Free Back Office Assessment
            </PrimaryCTA>
            <SecondaryCTA testid="hero-cta-secondary" to="/pricing">
              See Plans &amp; Pricing
            </SecondaryCTA>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-x-6 gap-y-3">
            {HERO_CHIPS.map((c) => (
              <div
                key={c}
                className="inline-flex items-center gap-2 text-[14px] text-slate-600"
              >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_18px_60px_rgba(22,160,106,0.18)] ring-1 ring-[#16A06A]/10">
            <img
              src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=1200&q=80"
              alt="Trades business owner on the job while RevGen handles the back office"
              loading="eager"
              className="w-full h-[440px] lg:h-[520px] object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/25 to-transparent" />
            <div
              data-testid="hero-stat-card"
              className="absolute left-5 bottom-5 w-[260px] rounded-xl bg-white border border-slate-200 card-soft px-4 py-3"
            >
              <div className="text-[32px] font-bold text-[#0A1628] leading-none">
                16 <span className="text-[16px] font-semibold text-slate-500">hrs/wk</span>
              </div>
              <p className="mt-2 text-[12px] leading-snug text-slate-600">
                Small business owners lose around 16 hours a week to admin.
              </p>
              <p className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Source: Time Etc
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* --------------------------------- Problem -------------------------------- */

const PROBLEMS = [
  {
    icon: FileSpreadsheet,
    title: "Books are a mess",
    body:
      "QuickBooks exports full of blank columns, misclassified COGS, no clean P&L. Breakeven is a guess.",
  },
  {
    icon: PhoneOff,
    title: "Zero post-call admin",
    body:
      "No follow-up emails sent. No agendas built. Recordings never uploaded. Client drift is constant.",
  },
  {
    icon: DollarSign,
    title: "Pricing built on gut",
    body:
      "Labor underpriced, no production rate math. Margins erode on every complex job.",
  },
  {
    icon: BarChart3,
    title: "No KPI visibility",
    body:
      "You can't answer: What's my pipeline? What's my close rate? What's my labor efficiency?",
  },
  {
    icon: Workflow,
    title: "CRM never configured",
    body:
      "GoHighLevel or HubSpot sits unused. Leads fall through. No follow-up sequences.",
  },
  {
    icon: UserX,
    title: "Growth stalls at you",
    body:
      "Estimating, invoicing, and scheduling all run through the owner. You can't scale past ~$1M.",
  },
];

const Problem = () => (
  <section id="problem" data-testid="home-problem" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Problem</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Trades owners are drowning in admin.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          You hired yourself a job — not a business. The back office is a
          black hole.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {PROBLEMS.map((p, i) => {
          const Icon = p.icon;
          return (
            <article
              key={p.title}
              data-testid={`home-problem-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={20} strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                {p.body}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ Five Pillars ------------------------------ */

const PILLARS = [
  {
    n: "01",
    title: "Financial Admin",
    bullets: [
      "QuickBooks cleanup & monthly close",
      "P&L formatting & chart-of-accounts alignment",
      "Breakeven tracking & variance reports",
    ],
  },
  {
    n: "02",
    title: "Call Operations",
    bullets: [
      "Post-call recap documents",
      "Follow-up email drafting",
      "Agenda prep for the next session",
    ],
  },
  {
    n: "03",
    title: "CRM & Lead Ops",
    bullets: [
      "GoHighLevel / HubSpot setup",
      "Google LSA & Google Business Profile management",
      "Lead pipeline maintenance",
    ],
  },
  {
    n: "04",
    title: "Pricing Support",
    bullets: [
      "Estimate sheet formatting",
      "Job cost template prep",
      "Margin tracking dashboards",
    ],
  },
  {
    n: "05",
    title: "HR & People Admin",
    bullets: [
      "Offer letter prep",
      "Onboarding checklists",
      "Commission plan documentation",
    ],
  },
];

const PillarCard = ({ p, i }) => (
  <article
    data-testid={`pillar-${p.n}`}
    className="reveal relative bg-white rounded-2xl border border-slate-200 card-soft p-7 hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
    style={{ transitionDelay: `${i * 60}ms` }}
  >
    <span className="absolute top-5 right-6 text-[42px] leading-none font-bold text-[#16A06A]/20">
      {p.n}
    </span>
    <h3 className="text-[19px] font-semibold text-[#0A1628]">{p.title}</h3>
    <ul className="mt-4 space-y-2.5">
      {p.bullets.map((b) => (
        <li key={b} className="flex items-start gap-2.5 text-[14px] text-slate-600">
          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F6EE]">
            <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
          </span>
          <span className="leading-relaxed">{b}</span>
        </li>
      ))}
    </ul>
  </article>
);

const Pillars = () => (
  <section id="pillars" data-testid="home-pillars" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">What We Do</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Your back office, done for you.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Five operating pillars. Every plan is built from them — you unlock
          more as you grow.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {PILLARS.slice(0, 3).map((p, i) => (
          <PillarCard key={p.n} p={p} i={i} />
        ))}
      </div>
      <div className="mt-5 grid md:grid-cols-2 gap-5 lg:max-w-[calc(66.6667%-0.625rem)] lg:mx-auto">
        {PILLARS.slice(3).map((p, i) => (
          <PillarCard key={p.n} p={p} i={i + 3} />
        ))}
      </div>

      <div
        data-testid="pillars-cta-band"
        className="reveal mt-12 rounded-2xl bg-[#E7F6EE] border border-[#16A06A]/20 p-6 lg:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <p className="text-[16px] lg:text-[17px] font-semibold text-[#0A1628]">
          Every plan is built from these five pillars.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
        >
          See exactly what each plan unlocks
          <ArrowRight
            size={16}
            className="transition-transform hover:translate-x-0.5"
          />
        </Link>
      </div>
    </div>
  </section>
);

/* ------------------------------ How It Works ------------------------------ */

const YOU_CONTROL = [
  "Priorities and deadlines",
  "What gets worked on",
  "Access to your tools and accounts",
  "Quality standards",
];
const WE_HANDLE = [
  "Recruiting and vetting",
  "Employment, payroll and benefits",
  "Training on trades workflows",
  "Quality assurance and backup coverage",
  "All the compliance paperwork",
];

const Node = ({ title, sub, accent = "ink", testid }) => (
  <div
    data-testid={testid}
    className={`flex-1 rounded-2xl border card-soft p-6 lg:p-7 text-center ${
      accent === "green"
        ? "bg-[#E7F6EE] border-[#16A06A]/20"
        : "bg-white border-slate-200"
    }`}
  >
    <div
      className={`mx-auto h-11 w-11 rounded-full flex items-center justify-center text-white ${
        accent === "green" ? "bg-[#16A06A]" : "bg-[#0A1628]"
      }`}
    >
      <BadgeCheck size={18} />
    </div>
    <div className="mt-3 text-[16px] font-bold text-[#0A1628]">{title}</div>
    <div className="mt-1 text-[13px] text-slate-500">{sub}</div>
  </div>
);

const Arrow = ({ label, direction = "right" }) => (
  <div className="flex items-center justify-center md:flex-col text-[#16A06A]">
    <div className="hidden md:flex flex-col items-center">
      <span className="eyebrow text-[#16A06A] whitespace-nowrap">{label}</span>
      <div className="mt-2">
        <ArrowRight
          size={26}
          strokeWidth={2.5}
          className={direction === "right" ? "" : "-scale-x-100"}
        />
      </div>
    </div>
    <div className="md:hidden flex flex-col items-center py-3">
      <span className="eyebrow text-[#16A06A]">{label}</span>
      <div className="mt-1 rotate-90">
        <ArrowRight
          size={22}
          strokeWidth={2.5}
          className={direction === "right" ? "" : "-scale-x-100"}
        />
      </div>
    </div>
  </div>
);

const HowItWorks = () => (
  <section
    id="how-it-works"
    data-testid="home-how"
    className="py-24 lg:py-28 bg-[#F8FAFC]"
  >
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">How It Works</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">We employ. You direct.</h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          You get a trained operator working inside your business — without
          payroll, contracts, hiring, or HR admin landing on your desk. We
          carry the employment. You set the priorities.
        </p>
      </div>

      <div className="reveal mt-12 rounded-2xl bg-white border border-slate-200 card-soft p-7 lg:p-10">
        <div className="flex flex-col md:flex-row md:items-stretch md:gap-4">
          <Node
            testid="how-node-owner"
            title="You"
            sub="Owner / Operator"
          />
          <Arrow label="Directs the work" direction="right" />
          <Node
            testid="how-node-operator"
            accent="green"
            title="Your RevGen Operator"
            sub="Works inside your tools"
          />
          <Arrow label="Employs · Trains · Pays" direction="left" />
          <Node testid="how-node-revgen" title="RevGen" sub="Employment & training" />
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-5">
        <div className="reveal rounded-2xl bg-white border border-slate-200 card-soft p-7">
          <Eyebrow tone="muted">You control</Eyebrow>
          <ul className="mt-5 space-y-3">
            {YOU_CONTROL.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] text-[#0A1628]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="reveal rounded-2xl bg-[#E7F6EE] border border-[#16A06A]/20 p-7">
          <Eyebrow tone="muted">RevGen handles</Eyebrow>
          <ul className="mt-5 space-y-3">
            {WE_HANDLE.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[15px] text-[#0A1628]">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#16A06A] text-white">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div
        data-testid="home-coverage-callout"
        className="reveal mt-8 rounded-2xl bg-[#0A1628] p-7 lg:p-9 text-white flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
      >
        <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#16A06A]">
          <BadgeCheck size={20} />
        </div>
        <p className="text-[16px] lg:text-[17px] leading-relaxed">
          If your operator isn't the right fit, or moves on — replacement is
          our problem, not yours. We place a replacement within two weeks, and
          every process is documented in your{" "}
          <span className="text-[#34D399] font-semibold">SOP library</span>,
          so nothing walks out the door.
        </p>
      </div>
    </div>
  </section>
);

/* ------------------------------ How We Staff ----------------------------- */

const STAFFING = [
  {
    icon: UserCheck,
    title: "You choose from a shortlist",
    body:
      "We recruit against your brief, score and screen the field, and bring you 3–5 Tier A candidates. You review resumes, meet them, and decide who you work with. No one gets assigned to you.",
  },
  {
    icon: Headphones,
    title: "A CS Manager on every account",
    body:
      "Your operator isn't left to manage themselves. A CS Manager holds weekly one-to-ones with them and syncs with you every week or two, so quality is managed from our side rather than policed from yours.",
  },
  {
    icon: CalendarCheck,
    title: "Live in about three weeks",
    body:
      "Scope call, then recruiting and screening across weeks one and two, then onboarding and a start date in week three — running full operations to your guidelines.",
  },
];

const HowWeStaff = () => (
  <section data-testid="home-staffing" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">How We Staff It</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          You pick your operator. We carry everything else.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {STAFFING.map((s, i) => {
          const Icon = s.icon;
          return (
            <article
              key={s.title}
              data-testid={`home-staffing-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={20} strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628] leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">
                {s.body}
              </p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ Plans Preview ----------------------------- */

const PLANS = [
  {
    name: "Starter",
    price: "$1,500",
    per: "/mo",
    capacity: "20 hrs/week",
    oneLiner: "Solo operator, 1–3 people",
    pillars: "Call Ops + core Financial Admin",
  },
  {
    name: "Growth",
    price: "$2,750",
    per: "/mo",
    capacity: "40 hrs/week",
    oneLiner: "5–15 people, $750K–$2M rev",
    pillars: "+ Full Financial Admin, CRM & Lead Ops",
    highlighted: true,
  },
  {
    name: "Operator",
    price: "$4,500",
    per: "/mo",
    capacity: "60 hrs/week",
    oneLiner: "15+ people, scaling past $2M",
    pillars: "+ Pricing Support, HR & People Admin",
  },
  {
    name: "Enterprise",
    price: "Custom",
    per: "",
    capacity: "Dedicated team",
    oneLiner: "Multi-location or $5M+",
    pillars: "All five, plus custom SOPs",
  },
];

const Plans = () => (
  <section data-testid="home-plans" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Plans</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Flat monthly pricing. Pick your depth.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Four plans, built from the same five pillars. No hourly billing, no
          setup fees.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map((p, i) => (
          <article
            key={p.name}
            data-testid={`plan-${p.name.toLowerCase()}`}
            className={`reveal relative flex flex-col rounded-2xl p-6 lg:p-7 border card-soft hover:-translate-y-1 transition-all ${
              p.highlighted
                ? "bg-white border-[#16A06A] ring-1 ring-[#16A06A]"
                : "bg-white border-slate-200"
            }`}
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            {p.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-[#16A06A] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 shadow-[0_4px_14px_rgba(22,160,106,0.35)]">
                Most Popular
              </span>
            )}
            <h3 className="text-[18px] font-semibold text-[#0A1628]">{p.name}</h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span
                className={`text-[30px] font-bold tracking-tight ${
                  p.price === "Custom" ? "text-[#0A1628]" : "text-[#16A06A]"
                }`}
              >
                {p.price}
              </span>
              {p.per && (
                <span className="text-[14px] font-medium text-slate-400">{p.per}</span>
              )}
            </div>
            <div className="mt-1 text-[13px] font-semibold text-slate-500">
              {p.capacity}
            </div>
            <p className="mt-4 text-[14px] leading-relaxed text-slate-600">
              {p.oneLiner}
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-slate-500 flex-1">
              {p.pillars}
            </p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] hover:text-[#0E7C53]"
            >
              See what's included
              <ArrowRight size={14} className="transition-transform hover:translate-x-1" />
            </Link>
          </article>
        ))}
      </div>

      <p
        data-testid="plans-projects-note"
        className="reveal mt-10 text-center text-[15px] text-slate-500"
      >
        Need something one-time instead?{" "}
        <Link
          to="/pricing#projects"
          className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
        >
          Fixed-fee projects from $400. →
        </Link>
      </p>

      <div className="reveal mt-6 text-center">
        <Link
          to="/calculator"
          data-testid="home-run-numbers-link"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-[14px] font-semibold text-[#0A1628] hover:border-[#16A06A] hover:text-[#16A06A] transition-colors"
        >
          Work out what your back office costs → <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </section>
);

/* ------------------------------ Comparison -------------------------------- */

const COMPARE_HEAD = ["Freelance VA", "General VA Agency", "RevGen"];
const COMPARE_ROWS = [
  ["Trades & construction-specific training", "x", "x", "check"],
  ["Knows QuickBooks, ServiceTitan, GoHighLevel", "Maybe", "Rarely", "check-always"],
  ["P&L cleanup & financial admin", "Basic", "Basic", "check-deep"],
  ["Built on advisor-level SOPs", "x", "x", "check"],
  ["Post-call recap documents", "x", "Rarely", "check-core"],
  ["Commission plan documentation", "x", "x", "check"],
  ["Breakeven & KPI tracking", "x", "x", "check"],
  ["Flat retainer (no hourly surprises)", "Hourly", "Hourly", "check-flat"],
  ["Coverage when your person leaves", "x", "Varies", "check-guaranteed"],
];

const RevGenCell = ({ value }) => {
  const labelMap = {
    check: null,
    "check-always": "Always",
    "check-deep": "Deep",
    "check-core": "Core offering",
    "check-flat": "Flat monthly",
    "check-guaranteed": "Replaced in 2 weeks",
  };
  const label = labelMap[value];
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#16A06A] text-white">
        <Check size={13} strokeWidth={3} />
      </span>
      {label && <span className="text-[#0A1628] font-medium">{label}</span>}
    </span>
  );
};

const OtherCell = ({ value }) => {
  if (value === "x") {
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-50 text-red-500">
        <X size={13} strokeWidth={3} />
      </span>
    );
  }
  return <span className="text-slate-500">{value}</span>;
};

const Comparison = () => (
  <section data-testid="home-comparison" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Difference</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Not a generalist. Trained in your business.
        </h2>
      </div>

      <div className="reveal mt-12 overflow-x-auto rounded-2xl bg-white border border-slate-200 card-soft">
        <table className="min-w-[820px] w-full text-left">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-5 md:px-8 py-5 text-[12px] md:text-[13px] font-semibold uppercase tracking-wider text-slate-500">
                Capability
              </th>
              {COMPARE_HEAD.map((h, i) => {
                const isRev = i === 2;
                return (
                  <th
                    key={h}
                    className={`px-5 md:px-8 py-5 text-[12px] md:text-[13px] font-semibold uppercase tracking-wider ${
                      isRev
                        ? "text-[#16A06A] bg-[#E7F6EE]"
                        : "text-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {h}
                      {isRev && (
                        <span className="inline-flex items-center rounded-full bg-[#16A06A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                          Recommended
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(([cap, a, b, rev]) => (
              <tr
                key={cap}
                className="border-t border-slate-200 text-[14px] md:text-[15px]"
              >
                <td className="px-5 md:px-8 py-5 md:py-6 font-semibold text-[#0A1628]">
                  {cap}
                </td>
                <td className="px-5 md:px-8 py-5 md:py-6">
                  <OtherCell value={a} />
                </td>
                <td className="px-5 md:px-8 py-5 md:py-6">
                  <OtherCell value={b} />
                </td>
                <td className="px-5 md:px-8 py-5 md:py-6 bg-[#E7F6EE]/60">
                  <RevGenCell value={rev} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="reveal mt-5 text-[12px] text-slate-400 max-w-4xl">
        RevGen is built specifically for trades and home services operators,
        on frameworks used with 7-figure businesses.
      </p>
    </div>
  </section>
);

/* ---------------------------- Reasons to Trust ---------------------------- */

const REASONS = [
  {
    hook: "Stop being the admin.",
    body:
      "You didn't build a $2M gutter company to spend Friday night uploading call recordings and cleaning QuickBooks. RevGen handles the ops overhead so you stay in the seat that moves the needle.",
    tag: "For the owner who's the bottleneck",
  },
  {
    hook: "Not a generalist.",
    body:
      "We speak QuickBooks, ServiceTitan, GoHighLevel, and Google LSA. Your operator knows what a job cost sheet looks like, what COGS vs. OpEx means, and how to prep a breakeven report.",
    tag: "Industry-trained, not general-trained",
  },
  {
    hook: "Built on proven frameworks.",
    body:
      "Our SOPs come from managing 20+ trades businesses. Every deliverable — from post-call recaps to P&L cleanup — is built on the same frameworks used with 7-figure operators.",
    tag: "Trained by advisors who've seen 20+ books",
  },
];

const Reasons = () => (
  <section data-testid="home-reasons" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Why It Works</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Three reasons owners trust RevGen.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {REASONS.map((r, i) => (
          <article
            key={r.hook}
            data-testid={`reason-${i + 1}`}
            className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <h3 className="text-[22px] font-bold tracking-tight text-[#0A1628] leading-snug">
              {r.hook}
            </h3>
            <p className="mt-4 text-[15px] leading-relaxed text-slate-500">
              {r.body}
            </p>
            <span className="mt-6 inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3 py-1 text-[12px] font-semibold text-[#16A06A]">
              {r.tag}
            </span>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    q: "Who actually employs the person doing my work?",
    a: "We do. RevGen recruits, employs, trains, and pays your operator. You direct their day-to-day work. You get the output without payroll, contracts, or HR admin landing on your side.",
  },
  {
    q: "What happens if my operator isn't a fit, or moves on?",
    a: "That's our problem, not yours. If the fit is wrong or your operator moves on, we replace them within two weeks and your CS Manager runs the handover. Every process we run for you is documented in your own SOP library, so nothing walks out the door with a person. Vacation and PTO are scheduled with you in advance; medical leave sits outside the replacement commitment and is handled case by case.",
  },
  {
    q: "Do I have to train them on my business?",
    a: "We handle the training. Your operator already works in QuickBooks, ServiceTitan, and GoHighLevel before they touch your account. Onboarding covers what's specific to you — your accounts, your job types, your standards.",
  },
  {
    q: "How many hours do I actually get?",
    a: "Starter is roughly 20 hours a week, Growth roughly 40, and Operator roughly 60 with a small dedicated team. You're buying scoped deliverables at a flat fee, not tracking a clock. If the work fits the scope, it gets done.",
  },
  {
    q: "What's not included?",
    a: "Anything outside your plan's pillars, and anything requiring a licensed CPA, attorney, or tax filing. We prepare and clean; we don't sign your return. Work outside scope can be quoted as a fixed-fee project.",
  },
  {
    q: "Can I start with a one-time project instead?",
    a: "Yes. Fixed-fee projects start at $400 — a financial review, a breakeven dashboard build, a CRM setup. Many owners start there and move to a monthly plan once they see the output.",
  },
  {
    q: "How fast can we start?",
    a: "Typically 2–3 weeks from scope call to your operator's first deliverable.",
  },
  {
    q: "Am I locked into a contract?",
    a: "No. Retainers renew monthly and cancel with 30 days' notice. There are no setup fees.",
  },
  {
    q: "Do you work inside my tools?",
    a: "Yes. Your operator works in your QuickBooks, your CRM, your Google Drive, your Slack. You keep ownership of every account.",
  },
  {
    q: "Do you replace my bookkeeper or CPA?",
    a: "No — we make them cheaper. We deliver clean, correctly classified books so your CPA spends time on strategy instead of cleanup.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="home-faq" className="py-24 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <Eyebrow tone="muted">FAQ</Eyebrow>
            <h2 className="h2-section mt-3 text-[#0A1628]">
              Questions owners actually ask.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Still unsure?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
              >
                Book a free assessment →
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 reveal">
            <div className="rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q} data-testid={`home-faq-item-${i + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`home-faq-toggle-${i + 1}`}
                      className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">
                        {f.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen
                            ? "bg-[#16A06A] text-white"
                            : "bg-[#E7F6EE] text-[#16A06A]"
                        }`}
                      >
                        {isOpen ? (
                          <Minus size={16} strokeWidth={3} />
                        ) : (
                          <Plus size={16} strokeWidth={3} />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA -------------------------------- */

const FinalCTA = () => (
  <section
    id="contact"
    data-testid="home-final-cta"
    className="relative bg-[#0A1628] overflow-hidden"
  >
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Get started</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Ready to get the admin off your desk?
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a free 30-minute back office assessment. We'll show you exactly
          what's costing you hours — no commitment.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <PrimaryCTA testid="home-final-cta-primary">
            Get a Free Back Office Assessment
          </PrimaryCTA>
          <Link
            to="/pricing"
            data-testid="home-final-cta-secondary"
            className="inline-flex items-center justify-center rounded-lg bg-transparent border border-white/30 hover:border-white text-white px-7 py-3.5 text-[15px] font-semibold transition-colors"
          >
            See Plans &amp; Pricing
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function Landing() {
  useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.title = "RevGen | Virtual Operations for Trades & Home Services";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "RevGen is a done-for-you virtual operations team built for trades and home services. Bookkeeping, CRM, call ops, job costing and HR admin handled. Flat plans from $1,500/mo."
      );
    }
  }, []);

  return (
    <div data-testid="landing-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Problem />
        <Pillars />
        <HowItWorks />
        <HowWeStaff />
        <Plans />
        <Comparison />
        <Reasons />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
